import { Show, createSignal } from 'solid-js';
import { Button, InputControl, Notice, Tabs } from '@academy/ui';
import { environmentStatus, getSupabase } from '../services/supabase';
import styles from './auth-page.module.css';

export const AuthPage = () => {
  const environment = environmentStatus();
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [message, setMessage] = createSignal<{ tone: 'error' | 'success'; text: string } | null>(
    null,
  );

  const signIn = async (event: SubmitEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const { error } = await getSupabase().auth.signInWithPassword({
      email: email(),
      password: password(),
    });
    setBusy(false);
    if (error) {
      setMessage({ tone: 'error', text: 'We could not sign you in with those details.' });
      return;
    }
    window.location.assign('/');
  };

  const sendMagicLink = async (event: SubmitEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const { error } = await getSupabase().auth.signInWithOtp({
      email: email(),
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    setBusy(false);
    setMessage(
      error
        ? { tone: 'error', text: 'The sign-in email could not be sent.' }
        : { tone: 'success', text: 'Check your inbox for a secure sign-in link.' },
    );
  };

  return (
    <main id="main-content" class={styles.page} tabindex="-1">
      <section class={styles.story} aria-labelledby="product-title">
        <div class={styles.wordmark}>
          <span class={styles.logo} aria-hidden="true">
            V
          </span>
          <span>Rubiano RealSpeak</span>
        </div>
        <div class={styles.storyCopy}>
          <p class={styles.eyebrow}>Language learning, in full voice</p>
          <h1 id="product-title">
            Calm classrooms.
            <br />
            Lively thinking.
          </h1>
          <p>
            Teach with live audio, purposeful visual stories, and responses that make every learner
            visible—without turning the lesson into a video grid.
          </p>
        </div>
        <div class={styles.promiseGrid}>
          <div>
            <strong>Audio first</strong>
            <span>Low-bandwidth, no participant camera.</span>
          </div>
          <div>
            <strong>Built to teach</strong>
            <span>Curriculum, evidence, and assessment connected.</span>
          </div>
        </div>
      </section>

      <section class={styles.authPanel} aria-labelledby="sign-in-title">
        <div class={styles.authCard}>
          <p class={styles.eyebrow}>Welcome back</p>
          <h2 id="sign-in-title">Enter your academy</h2>
          <p class={styles.lead}>Use the account supplied by your academy.</p>

          {!environment.configured && (
            <Notice tone="warning" title="Local configuration required">
              <p>
                Add {environment.missing.join(' and ')} to an uncommitted <code>.env.local</code>.
              </p>
            </Notice>
          )}

          <Tabs defaultValue="password" class={styles.tabs}>
            <Tabs.List class={styles.tabList} aria-label="Sign-in method">
              <Tabs.Trigger class={styles.tab} value="password">
                Password
              </Tabs.Trigger>
              <Tabs.Trigger class={styles.tab} value="link">
                Email link
              </Tabs.Trigger>
              <Tabs.Indicator class={styles.indicator} />
            </Tabs.List>
            <Tabs.Content value="password">
              <form class={styles.form} onSubmit={(event) => void signIn(event)}>
                <label>
                  Email
                  <InputControl
                    type="email"
                    autocomplete="email"
                    required
                    value={email()}
                    onInput={(event) => setEmail(event.currentTarget.value)}
                  />
                </label>
                <label>
                  Password
                  <InputControl
                    type="password"
                    autocomplete="current-password"
                    minlength="12"
                    required
                    value={password()}
                    onInput={(event) => setPassword(event.currentTarget.value)}
                  />
                </label>
                <Button type="submit" disabled={busy() || !environment.configured}>
                  {busy() ? 'Signing in…' : 'Sign in securely'}
                </Button>
              </form>
            </Tabs.Content>
            <Tabs.Content value="link">
              <form class={styles.form} onSubmit={(event) => void sendMagicLink(event)}>
                <label>
                  Email
                  <InputControl
                    type="email"
                    autocomplete="email"
                    required
                    value={email()}
                    onInput={(event) => setEmail(event.currentTarget.value)}
                  />
                </label>
                <p class={styles.hint}>We will send a single-use link to this address.</p>
                <Button type="submit" disabled={busy() || !environment.configured}>
                  {busy() ? 'Sending…' : 'Email a sign-in link'}
                </Button>
              </form>
            </Tabs.Content>
          </Tabs>
          <Show when={message()}>
            {(current) => (
              <Notice
                tone={current().tone}
                title={current().tone === 'error' ? 'Sign-in problem' : 'Email sent'}
              >
                <p>{current().text}</p>
              </Notice>
            )}
          </Show>
          <p class={styles.privacy}>
            Your session, permissions, and MFA are managed by the academy’s secure identity service.
          </p>
        </div>
      </section>
    </main>
  );
};
